import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { TextField, Button, Box, Typography } from '@mui/material';
import GitHubAppInstallationShare from './GitHubAppInstallationShare';
import { useDeleteGitHubInstallation, useUpdateGitHubInstallation } from '../hooks/useGitHubInstallations';
import { useTenants } from '../hooks/useTenants';
import '../styles/shared.scss';

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    name: string;
    description?: string;
    updatedAt: string;
    isShared?: boolean;
    sharedBy?: string;
    sharedAt?: string;
}

interface ConnectionEditFormProps {
    editData: { name: string; description: string };
    setEditData: (data: { name: string; description: string }) => void;
    selectedInstallation: GitHubInstallation;
    onSave: () => Promise<void>;
    onDelete: () => Promise<void>;
    editorActions?: any;
    tenants: any[];
    isReadOnly?: boolean;
}

const ConnectionEditForm: React.FC<ConnectionEditFormProps> = ({
    editData,
    setEditData,
    selectedInstallation,
    onSave,
    onDelete,
    editorActions,
    tenants,
    isReadOnly = false
}) => {
    useEffect(() => {
        if (editorActions) {
            editorActions.onSave = onSave;
            editorActions.onDelete = onDelete;
        }
    }, [editorActions, onSave, onDelete]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isReadOnly && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        This is a shared connection. You can view details but cannot modify it.
                    </Typography>
                </Box>
            )}
            
            <TextField
                label="Connection Name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                fullWidth
                required
                disabled={isReadOnly}
            />

            <TextField
                label="Description"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                disabled={isReadOnly}
            />

            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Installation Details (Read-only)</Typography>

                <TextField
                    label="Account Login"
                    value={selectedInstallation.accountLogin}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Account Type"
                    value={selectedInstallation.accountType}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Repository Selection"
                    value={selectedInstallation.repositorySelection}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Installation ID"
                    value={selectedInstallation.installationId}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />
            </Box>

            {!isReadOnly && (
                <Box sx={{ mt: 3 }} onClick={(e) => e.stopPropagation()}>
                    <Typography variant="h6" gutterBottom>Tenant Shares</Typography>
                    <GitHubAppInstallationShare
                        installation={selectedInstallation}
                        tenants={tenants}
                        onCancel={() => {}}
                        onSuccess={() => {}}
                    />
                </Box>
            )}
        </Box>
    );
};

interface GitHubAppManagementProps {
    selectedInstallation?: GitHubInstallation;
    isReadOnly?: boolean;
}

const GitHubAppManagement: React.FC<GitHubAppManagementProps> = ({ selectedInstallation, isReadOnly = false }) => {
    const { data: tenants = [] } = useTenants();
    const updateInstallation = useUpdateGitHubInstallation();
    const deleteInstallation = useDeleteGitHubInstallation();
    const [editData, setEditData] = useState({ name: '', description: '' });
    const [editorActions] = useState(() => ({}));

    useEffect(() => {
        if (selectedInstallation) {
            setEditData({
                name: selectedInstallation.name || '',
                description: selectedInstallation.description || ''
            });
        }
    }, [selectedInstallation]);

    const handleSave = async () => {
        if (!selectedInstallation || isReadOnly) return;
        
        try {
            await updateInstallation.mutateAsync({
                connectionId: selectedInstallation.connectionId,
                updates: {
                    name: editData.name,
                    description: editData.description
                }
            });
            console.log('Installation updated successfully');
        } catch (error) {
            console.error('Failed to update installation:', error);
            throw error;
        }
    };

    const handleDelete = async () => {
        if (!selectedInstallation || isReadOnly) return;
        
        try {
            await deleteInstallation.mutateAsync(selectedInstallation.connectionId);
        } catch (error) {
            console.error('Failed to delete installation:', error);
        }
    };

    if (selectedInstallation) {
        return (
            <ConnectionEditForm
                key={selectedInstallation.connectionId}
                editData={editData}
                setEditData={setEditData}
                selectedInstallation={selectedInstallation}
                onSave={handleSave}
                onDelete={handleDelete}
                editorActions={editorActions}
                tenants={tenants}
                isReadOnly={isReadOnly}
            />
        );
    }

    const handleConnectGitHub = () => {
        const appSlug = process.env.REACT_APP_GITHUB_APP_SLUG || 'locomotive10-product-catalog-dev';
        const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
        const applicationName = process.env.REACT_APP_APPLICATION_NAME || 'servicecatalog';
        const domain = process.env.REACT_APP_DOMAIN || 'rainmaking.cloud';
        const callbackUrl = `https://www.${environment}.${applicationName}.${domain}/integrations/github/callback`;
        const state = encodeURIComponent(JSON.stringify({ name: 'My GitHub Connection', description: '' }));
        const installUrl = `https://github.com/apps/${appSlug}/installations/new?state=${state}&redirect_url=${encodeURIComponent(callbackUrl)}`;

        window.location.href = installUrl;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Create GitHub App Connection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your GitHub account to enable app-based access to repositories.
            </Typography>
            <Button
                variant="contained"
                onClick={handleConnectGitHub}
                startIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                size="large"
            >
                Connect GitHub App
            </Button>
        </Box>
    );
};

export default GitHubAppManagement;